// F-CM-009: case-opening UI panel.
//
// Mounts the existing `CaseOpener` animation controller onto a fullscreen
// overlay. Lists every active case from `WEAPON_CASES`, lets the player click
// "OPEN" on a case, then drives `CaseOpener.update(dt)` from a RAF loop and
// renders the reel + reveal stages.
//
// Server endpoint note: the audit's intended flow is "actual roll happens
// SERVER-SIDE; client just animates to the result." There is no
// `POST /api/cases/open` endpoint yet — when one ships, replace `rollLocally`
// with a fetch to it. The local roll uses the same drop pool weights and
// `STATTRAK_CHANCE` constant so the visual result is faithful.

import {
  WEAPON_CASES,
  RARITY_DROP_RATES,
  STATTRAK_CHANCE,
  RARITY_INFO,
  type CaseDropEntry,
  type CaseDropPool,
  type CaseOpenResult,
  type UnboxPhase,
  type WeaponCase,
} from '../cosmetics/weapon-case-types';
import { wearLevelDisplayName, wearLevelFromFloat } from '../cosmetics/weapon-skin-types';
import { CaseOpener } from '../cosmetics/weapon-case-opener';
import { weaponSkinRegistry } from '../cosmetics/weapon-skin-registry';

const REEL_SPEEDUP_FACTOR = 5 / 3.5;

export class CaseOpenerPanel {
  private container: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  private stageEl: HTMLElement | null = null;
  private resultEl: HTMLElement | null = null;
  private reelTrackEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private backBtn: HTMLElement | null = null;
  private closeStageBtn: HTMLElement | null = null;
  private skipStageBtn: HTMLElement | null = null;
  private opener: CaseOpener = new CaseOpener();
  private rafHandle: number | null = null;
  private lastFrameMs = 0;
  private visible = false;
  private onBack: (() => void) | null = null;
  private boundBack: (() => void) | null = null;
  private boundCloseStage: (() => void) | null = null;
  private boundSkipStage: (() => void) | null = null;

  init(parent: HTMLElement): void {
    if (this.container) return;
    const el = document.createElement('div');
    el.id = 'case-opener-panel';
    el.style.cssText = [
      'position:fixed', 'inset:0', 'background:#0a0d12',
      'color:#e7eaee', 'font-family:Inter,system-ui,sans-serif',
      'display:none', 'flex-direction:column', 'z-index:1100',
    ].join(';');
    el.innerHTML = `
      <header style="display:flex;align-items:center;gap:16px;padding:16px 24px;border-bottom:1px solid #1d2230;">
        <button data-role="back" style="background:transparent;border:1px solid #2a3142;color:#e7eaee;padding:8px 16px;cursor:pointer;font-weight:600;">‹ BACK</button>
        <h2 style="margin:0;font-size:20px;letter-spacing:1px;">CASES</h2>
        <span data-role="status" style="margin-left:auto;color:#8e96a3;font-size:12px;"></span>
      </header>
      <!-- U-H-004 / W-M-004: case-opener is preview-only until POST /api/cases/open ships -->
      <div data-role="preview-banner" style="padding:12px 24px;background:rgba(255,106,0,0.08);border-bottom:1px solid rgba(255,106,0,0.25);color:#ff6a00;font-size:12px;letter-spacing:1px;text-align:center;text-transform:uppercase;">
        Server endpoint pending — case opening is disabled until POST /api/cases/open ships
      </div>
      <div data-role="list" style="flex:1;overflow:auto;padding:24px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;"></div>
      <div data-role="stage" style="display:none;flex-direction:column;align-items:center;justify-content:center;flex:1;padding:32px;background:linear-gradient(180deg,#10141d 0%,#070a10 100%);overflow-y:auto;-webkit-overflow-scrolling:touch;">
        <div data-role="reel-frame" style="position:relative;width:min(960px,90vw);height:160px;overflow:hidden;border:2px solid #2a3142;border-radius:8px;background:#0c0f17;">
          <div data-role="reel-track" style="position:absolute;top:0;left:50%;display:flex;height:100%;will-change:transform;"></div>
          <div data-role="reel-marker" style="position:absolute;top:0;left:50%;width:2px;height:100%;background:#ff6a00;transform:translateX(-1px);box-shadow:0 0 8px #ff6a00;"></div>
        </div>
        <div data-role="result" style="margin-top:32px;min-height:120px;text-align:center;"></div>
        <div style="margin-top:16px;display:flex;gap:10px;flex-wrap:wrap;justify-content:center;">
          <button data-role="skip-stage" style="background:#2a3142;border:1px solid #3b465e;color:#e7eaee;padding:10px 24px;cursor:pointer;font-weight:600;display:none;">SKIP</button>
          <button data-role="close-stage" style="background:#1f2a3d;border:1px solid #2a3142;color:#e7eaee;padding:10px 24px;cursor:pointer;font-weight:600;display:none;">CLOSE</button>
        </div>
      </div>
    `;
    parent.appendChild(el);

    this.container = el;
    this.listEl = el.querySelector('[data-role="list"]');
    this.stageEl = el.querySelector('[data-role="stage"]');
    this.resultEl = el.querySelector('[data-role="result"]');
    this.reelTrackEl = el.querySelector('[data-role="reel-track"]');
    this.statusEl = el.querySelector('[data-role="status"]');
    this.backBtn = el.querySelector('[data-role="back"]');
    this.closeStageBtn = el.querySelector('[data-role="close-stage"]');
    this.skipStageBtn = el.querySelector('[data-role="skip-stage"]');

    this.boundBack = () => {
      this.hide();
      this.onBack?.();
    };
    this.backBtn?.addEventListener('click', this.boundBack);

    this.boundCloseStage = () => this.exitStage();
    this.closeStageBtn?.addEventListener('click', this.boundCloseStage);

    this.boundSkipStage = () => this.skipToReveal();
    this.skipStageBtn?.addEventListener('click', this.boundSkipStage);

    this.opener.setCallbacks(
      (phase) => this.onPhaseChange(phase),
      (result) => this.onComplete(result),
    );
  }

  setOnBack(cb: () => void): void {
    this.onBack = cb;
  }

  show(): void {
    if (!this.container) return;
    this.visible = true;
    this.container.style.display = 'flex';
    this.renderList();
  }

  hide(): void {
    if (!this.container) return;
    this.visible = false;
    this.container.style.display = 'none';
    this.exitStage();
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.boundBack) this.backBtn?.removeEventListener('click', this.boundBack);
    if (this.boundCloseStage) this.closeStageBtn?.removeEventListener('click', this.boundCloseStage);
    if (this.boundSkipStage) this.skipStageBtn?.removeEventListener('click', this.boundSkipStage);
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    this.container?.remove();
    this.container = null;
  }

  private renderList(): void {
    if (!this.listEl) return;
    this.listEl.innerHTML = '';
    for (const wc of WEAPON_CASES) {
      if (!wc.active) continue;
      const card = document.createElement('div');
      card.style.cssText = 'background:#10141d;border:1px solid #1d2230;border-radius:8px;padding:16px;display:flex;flex-direction:column;gap:8px;';
      // U-H-004 / W-M-004: button disabled until POST /api/cases/open ships;
      // the existing rollLocally() + animation code stays in place so the
      // panel can be re-enabled by removing `disabled` and re-wiring the
      // click handler once the server endpoint exists.
      card.innerHTML = `
        <div style="font-weight:700;font-size:16px;">${escapeHtml(wc.name)}</div>
        <div style="color:#8e96a3;font-size:12px;line-height:1.4;">${escapeHtml(wc.description)}</div>
        <div style="margin-top:auto;display:flex;align-items:center;justify-content:space-between;">
          <span style="color:#ff6a00;font-weight:700;">$${wc.keyPrice}</span>
          <button data-case-id="${wc.id}" disabled aria-disabled="true" title="Server endpoint pending" style="background:#3a3a3a;border:none;color:#7a7a7a;padding:8px 16px;cursor:not-allowed;font-weight:700;opacity:0.6;">SOON<span style="display:block;font-size:11px;opacity:0.6;">(server endpoint pending)</span></button>
        </div>
      `;
      this.listEl.appendChild(card);
    }
  }

  private beginOpen(wc: WeaponCase): void {
    if (!this.stageEl || !this.listEl || !this.reelTrackEl) return;
    const result = this.rollLocally(wc);
    if (!result) {
      this.setStatus('No drops available for this case.');
      return;
    }
    this.listEl.style.display = 'none';
    this.stageEl.style.display = 'flex';
    if (this.closeStageBtn) this.closeStageBtn.style.display = 'none';
    if (this.skipStageBtn) this.skipStageBtn.style.display = 'inline-block';
    if (this.resultEl) this.resultEl.innerHTML = '';
    this.reelTrackEl.innerHTML = '';
    this.lastFrameMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this.opener.startOpen(wc, result);
    this.populateReel();
    this.startLoop();
  }

  private populateReel(): void {
    if (!this.reelTrackEl) return;
    this.reelTrackEl.innerHTML = '';
    for (const item of this.opener.currentReelItems) {
      const cell = document.createElement('div');
      const color = RARITY_INFO[item.rarity]?.color ?? '#888';
      cell.style.cssText = `flex:0 0 120px;height:160px;border-right:1px solid #1d2230;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px;background:${hexToBg(color)};`;
      cell.innerHTML = `
        <div style="font-size:11px;color:#8e96a3;text-transform:uppercase;">${escapeHtml(item.weaponId)}</div>
        <div style="font-size:13px;font-weight:600;text-align:center;margin-top:4px;color:${color};">${escapeHtml(item.name)}</div>
      `;
      this.reelTrackEl.appendChild(cell);
    }
  }

  private startLoop(): void {
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    const tick = () => {
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const dt = Math.min(0.1, Math.max(0, (now - this.lastFrameMs) / 1000));
      this.lastFrameMs = now;
      const phase = this.opener.currentPhase;
      const reelDt = phase === 'scrolling' || phase === 'decelerating'
        ? dt * REEL_SPEEDUP_FACTOR
        : dt;
      this.opener.update(reelDt);
      this.applyReelPosition();
      if (this.opener.currentPhase !== 'complete' && this.visible) {
        this.rafHandle = requestAnimationFrame(tick);
      } else {
        this.rafHandle = null;
      }
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private applyReelPosition(): void {
    if (!this.reelTrackEl) return;
    // Center the reel on the current scroll position. itemWidth = 120 (mirrors CaseOpener constant).
    const offsetPx = -this.opener.currentReelPosition - 60; // -60 = half-cell so the marker sits in cell centre
    this.reelTrackEl.style.transform = `translateX(${offsetPx}px)`;
  }

  private skipToReveal(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    for (let i = 0; i < 8 && this.opener.currentPhase !== 'complete'; i++) {
      this.opener.update(10);
    }
    this.applyReelPosition();
    if (this.skipStageBtn) this.skipStageBtn.style.display = 'none';
  }

  private onPhaseChange(phase: UnboxPhase): void {
    this.setStatus(phase.toUpperCase());
  }

  private onComplete(result: CaseOpenResult): void {
    if (!this.resultEl) return;
    const skin = weaponSkinRegistry.getSkin(result.skinId);
    if (!skin) {
      this.resultEl.innerHTML = '<div style="color:#ff6a6a;">Result skin missing from registry.</div>';
      if (this.closeStageBtn) this.closeStageBtn.style.display = 'inline-block';
      return;
    }
    const color = RARITY_INFO[skin.rarity]?.color ?? '#888';
    const wearName = wearLevelDisplayName(wearLevelFromFloat(result.wearFloat));
    this.resultEl.innerHTML = `
      <div style="font-size:14px;color:#8e96a3;text-transform:uppercase;letter-spacing:2px;">YOU UNBOXED</div>
      <div style="font-size:22px;font-weight:800;color:${color};margin-top:8px;">
        ${result.isStatTrak ? '<span style="color:#ff6a00;">StatTrak™</span> ' : ''}${escapeHtml(skin.weaponIds[0] ?? '')} | ${escapeHtml(skin.name)}
      </div>
      <div style="font-size:13px;color:#cdd2db;margin-top:8px;">${escapeHtml(wearName)} (${result.wearFloat.toFixed(3)}) · pattern #${result.patternSeed}</div>
    `;
    if (this.closeStageBtn) this.closeStageBtn.style.display = 'inline-block';
    if (this.skipStageBtn) this.skipStageBtn.style.display = 'none';
  }

  private exitStage(): void {
    if (!this.stageEl || !this.listEl) return;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    this.opener.reset();
    this.stageEl.style.display = 'none';
    this.listEl.style.display = 'grid';
    if (this.skipStageBtn) this.skipStageBtn.style.display = 'none';
    this.setStatus('');
  }

  private setStatus(text: string): void {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  /** Local fallback that mirrors the documented server roll until a
   *  `POST /api/cases/open` endpoint exists. Picks a rarity tier per
   *  RARITY_DROP_RATES, then a weighted skin within that tier, then rolls
   *  StatTrak per STATTRAK_CHANCE. wear/pattern are uniform-random. */
  private rollLocally(wc: WeaponCase): CaseOpenResult | null {
    // Pick rarity weighted by RARITY_DROP_RATES, but only across tiers that
    // have entries in this case so empty tiers don't waste rolls.
    const specialRoll = Math.random();
    let entry: CaseDropEntry | undefined;
    let rarityKey: keyof CaseDropPool | 'special' = 'consumer';
    if (specialRoll < wc.specialRate && wc.specialPool.length > 0) {
      entry = pickWeighted(wc.specialPool);
      rarityKey = 'special';
    } else {
      const orderedTiers: Array<keyof CaseDropPool> = ['covert', 'classified', 'restricted', 'mil-spec', 'industrial', 'consumer'];
      const totalWeight = orderedTiers.reduce((sum, tier) => sum + (wc.dropPool[tier].length > 0 ? RARITY_DROP_RATES[tier] : 0), 0);
      if (totalWeight <= 0) return null;
      let r = Math.random() * totalWeight;
      for (const tier of orderedTiers) {
        if (wc.dropPool[tier].length === 0) continue;
        r -= RARITY_DROP_RATES[tier];
        if (r <= 0) {
          entry = pickWeighted(wc.dropPool[tier]);
          rarityKey = tier;
          break;
        }
      }
    }
    if (!entry) return null;
    const skin = weaponSkinRegistry.getSkin(entry.skinId);
    return {
      skinId: entry.skinId,
      wearFloat: Math.random(),
      patternSeed: Math.floor(Math.random() * 1000),
      isStatTrak: !!skin?.statTrakEligible && Math.random() < STATTRAK_CHANCE,
      rarity: rarityKey === 'special' ? 'covert' : rarityKey,
      instanceId: `local-${wc.id}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
      skinName: skin?.name ?? `Skin ${entry.skinId}`,
      weaponId: skin?.weaponIds[0] ?? '',
    };
  }
}

function pickWeighted(entries: CaseDropEntry[]): CaseDropEntry | undefined {
  if (entries.length === 0) return undefined;
  const total = entries.reduce((s, e) => s + Math.max(0, e.weight), 0);
  if (total <= 0) return entries[0];
  let r = Math.random() * total;
  for (const e of entries) {
    r -= Math.max(0, e.weight);
    if (r <= 0) return e;
  }
  return entries[entries.length - 1];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function hexToBg(hex: string): string {
  // Render a faint tinted background from the rarity colour.
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return '#10141d';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.08)`;
}
